import {createContext, useContext, useEffect, useState} from 'react'
import {useLocation, useNavigate} from 'react-router'

import type {DepartmentNode} from '@refra/modules/common/types'
import {useUrlParam, useUrlParamOptional} from '@refra/modules/routing/hooks'

interface KilnsContext {
  selectedKiln: DepartmentNode
  kilnNodes: DepartmentNode[]
  setSelectedKiln: (value: DepartmentNode) => void
}

const KilnsContext = createContext<KilnsContext | undefined>(undefined)

export const useKilns = () => {
  const context = useContext(KilnsContext)
  if (!context) throw new Error('useKilns is used outside of the KilnsProvider!')
  return context
}

interface KilnsProviderProps {
  kilnNodes: DepartmentNode[]
  children: React.ReactNode
  shouldRedirectToUrlKiln?: boolean
}

export const KilnsProvider: React.FC<KilnsProviderProps> = ({
  children,
  kilnNodes,
  shouldRedirectToUrlKiln
}) => {
  const selectedKilnId = useUrlParamOptional('selectedKilnId')
  const plantCode = useUrlParam('plantCode')
  const kilnById = kilnNodes.find((kilnNode) => kilnNode.upmId === selectedKilnId)
  const [selectedKiln, setSelectedKiln] = useState<DepartmentNode>(kilnById ?? kilnNodes[0])
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!shouldRedirectToUrlKiln) return
    if (!kilnNodes || kilnNodes.length === 0) return

    if (location.pathname.includes(selectedKiln.upmId) && !selectedKilnId) {
      void navigate(`/plants/${plantCode}/static-parts`, {relative: 'path', replace: true})
      return
    }

    if (selectedKiln.upmId !== selectedKilnId) {
      const navigateTo = selectedKilnId ? `../${selectedKiln.upmId}` : `${selectedKiln.upmId}`
      void navigate(navigateTo, {relative: 'path', replace: true})
    }
  })

  return (
    <KilnsContext.Provider value={{selectedKiln, setSelectedKiln, kilnNodes}}>
      {children}
    </KilnsContext.Provider>
  )
}
